// Pig Mountain — the art (LB-256/LB-386, zip 2 of docs/PIG_MOUNTAIN_SCOPE.md §6).
//
// Claude Design's hand-off (2 Oct 2026) is one sprite, public/pig-mountain-sprite.svg, holding
// 194 <symbol>s in two coordinate systems:
//
//   characters, wearables, sleds, the Crow   0 0 200 260, the same space and anchors as
//                                            piggington-sprite.svg, so the app's dressing rule
//                                            (base, then cosmetics ascending by z) carries over
//   game pieces and scenery (dd-*)           their own viewBox at 100 units = 1 metre, anchored
//                                            bottom-centre where the piece meets the snow
//
// The rider is DOM (<use> into the sprite, like <Piggington>). Everything else is painted on
// the game's canvas, and a canvas cannot <use> a symbol, so loadPmArt() reads the sprite once,
// lifts each dd-*/cw-* symbol into a standalone image and rasterises it. Until that has
// happened (or if the sprite cannot be fetched: first open with no signal and no cache) the
// renderer keeps its placeholder shapes — pigMountainDraw.js asks for a piece and paints the
// placeholder when it gets null. The game never waits on art.

export const PM_SPRITE_SRC = "/pig-mountain-sprite.svg";

// The app's cosmetics seen from behind: app id -> [back symbol, z]. Five are hidden by the head
// or neck from behind and are skipped (monocle, eyebrows, eyelashes, bow tie, tie).
// check-pigmountain.mjs holds this table against PIGGINGTON_COSMETICS and the sprite, so a new
// cosmetic cannot ship without a decision here.
export const PM_BACK_WEARABLES = {
  "cos-socks": ["cos-socks-back", 4],
  "cos-shoes": ["cos-shoes-back", 5],
  "cos-slippers": ["cos-slippers-back", 5],
  "cos-hair": ["cos-hair-back", 6],
  "cos-hair-long-blonde": ["cos-hair-long-blonde-back", 7],
  "cos-hair-long-brown": ["cos-hair-long-brown-back", 7],
  "cos-tee": ["cos-tee-back", 20],
  "cos-shorts": ["cos-shorts-back", 21],
  "cos-coat": ["cos-coat-back", 22],
  "cos-jumper": ["cos-jumper-back", 22],
  "cos-pyjamas": ["cos-pyjamas-back", 23],
  "cos-dress": ["cos-dress-back", 23],
  "cos-skirt": ["cos-skirt-back", 24],
  "cos-glasses": ["cos-glasses-back", 30],
  "cos-coin": ["cos-coin-back", 35],
  "cos-gloves": ["cos-gloves-back", 36],
  "cos-mittens": ["cos-mittens-back", 36],
  "cos-held-bank": ["cos-held-bank-back", 37],
  "cos-held-umbrella": ["cos-held-umbrella-back", 37],
  "cos-held-balloon": ["cos-held-balloon-back", 37],
  "cos-held-book": ["cos-held-book-back", 37],
  "cos-bowler": ["cos-bowler-back", 40],
  "cos-party-hat": ["cos-party-hat-back", 40],
  "cos-wooly-hat": ["cos-wooly-hat-back", 40],
  "cos-baseball-cap": ["cos-cap-back", 40],
  "cos-crown": ["cos-crown-back", 40],
  "cos-mohawk": ["cos-mohawk-back", 41],
  "cos-headphones": ["cos-headphones-back", 45],
  "cos-ear-muffs": ["cos-ear-muffs-back", 45],
};
export const PM_HIDDEN_FROM_BEHIND = ["cos-monocle", "cos-eyebrows", "cos-eyelashes", "cos-bow-tie", "cos-tie"];

// The worn cosmetics as back symbols, in drawing order.
export function backWearables(cosmetics) {
  return (cosmetics || [])
    .map((id) => PM_BACK_WEARABLES[id])
    .filter(Boolean)
    .sort((a, b) => a[1] - b[1])
    .map((w) => w[0]);
}

// Every piece the canvas renderer names. check-pigmountain.mjs holds each against the sprite.
export const PM_CANVAS_PIECES = [
  "dd-rock-1", "dd-rock-2", "dd-log", "dd-pine-small", "dd-welly", "dd-anvil",
  "dd-skier-1", "dd-skier-2", "dd-skier-1-b", "dd-skier-2-b", "dd-skier-1-c", "dd-skier-2-c", "dd-penguin-slide",
  "dd-snowball-giant-1", "dd-snowball-giant-2", "dd-player-snowball-1", "dd-player-snowball-2",
  "dd-ramp", "dd-mogul", "dd-crevasse-lip", "dd-boost-arrows", "dd-boost-can", "dd-snowball-stack",
  "dd-tin-hat", "dd-rocket", "dd-snowball-powerup", "dd-pickup-marker", "dd-top-hat",
  "dd-gate-marigold", "dd-gate-red", "dd-start-banner", "dd-finish-banner", "dd-best-flag-marigold", "dd-best-flag-blue",
  "dd-pine-1", "dd-pine-2", "dd-pine-3", "dd-pine-snowy-1", "dd-pine-snowy-2",
  "dd-lodge-1", "dd-lodge-2", "dd-lodge-3", "dd-lodge-night",
  "dd-spectator-1", "dd-spectator-2", "dd-spectator-3", "dd-spectator-4", "dd-spectator-5", "dd-skater-1", "dd-skater-2",
  "dd-snowman", "dd-piste-sign-1", "dd-piste-sign-2", "dd-piste-sign-buried", "dd-edge-pole", "dd-lantern-post",
  "dd-ice-pillar-1", "dd-ice-pillar-2", "dd-ice-pillar-3", "dd-icicles",
  "dd-backdrop-forest", "dd-backdrop-lake", "dd-backdrop-night", "dd-backdrop-blizzard", "dd-backdrop-cave",
  "cw-back-fly-1", "cw-back-fly-2", "cw-back-fly-3", "cw-back-fly-nohat", "cw-back-hit", "cw-back-drop",
];

// Raster size: pieces are drawn at up to about 60 CSS px per metre on a phone, so 1.3 px per
// unit (130 px per metre) covers a 2x screen. The cap keeps the big ones (banners, backdrops,
// the giant snowball) from costing more memory than they are worth.
const PX_PER_UNIT = 1.3;
const MAX_PX = 1200;

let loading = null;
const pieces = new Map();

// A piece by id: { c: canvas, w, h } with w and h in metres (units / 100), or null when the art
// is not (yet) loaded.
export function pmPiece(id) {
  return pieces.get(id) || null;
}

function rasterise(id, vbW, vbH, body) {
  return new Promise((resolve) => {
    const k = Math.min(PX_PER_UNIT, MAX_PX / Math.max(vbW, vbH));
    const pw = Math.max(1, Math.round(vbW * k)), ph = Math.max(1, Math.round(vbH * k));
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pw}" height="${ph}" viewBox="0 0 ${vbW} ${vbH}">${body}</svg>`;
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = pw; c.height = ph;
        c.getContext("2d").drawImage(img, 0, 0, pw, ph);
        pieces.set(id, { c, w: vbW / 100, h: vbH / 100 });
      } catch { /* this piece keeps its placeholder */ }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

// Reads the sprite and rasterises the canvas pieces. Resolves true when the art is usable,
// false when it is not (the renderer then keeps its placeholders). Safe to call repeatedly.
export function loadPmArt() {
  if (loading) return loading;
  if (typeof fetch !== "function" || typeof Image === "undefined" || typeof document === "undefined") return Promise.resolve(false);
  loading = fetch(PM_SPRITE_SRC)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error("sprite"))))
    .then((text) => {
      const want = new Set(PM_CANVAS_PIECES);
      const jobs = [];
      const re = /<symbol id="([^"]+)" viewBox="0 0 ([\d.]+) ([\d.]+)">([\s\S]*?)<\/symbol>/g;
      let m;
      while ((m = re.exec(text))) if (want.has(m[1])) jobs.push(rasterise(m[1], Number(m[2]), Number(m[3]), m[4]));
      return Promise.all(jobs);
    })
    .then(() => pieces.size > 0)
    .catch(() => { loading = null; return false; });
  return loading;
}
